import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  BriefcaseBusiness,
  CheckCircle2,
  ChevronDown,
  CircleHelp,
  Headphones,
  LifeBuoy,
  MessageCircle,
  Paperclip,
  Send,
  Wrench,
  X,
  ChevronRight,
  CornerDownLeft,
  Mail,
  Phone,
  ExternalLink,
} from "lucide-react";

import { api } from "@/lib/api";
import ScreenOverlay from "../components/common/ScreenOverlay";

const SIMPLER_WHATSAPP_DISPLAY = "(11) 92615-0033";
const SIMPLER_WHATSAPP_URL =
  "https://wa.me/5511926150033?text=Ol%C3%A1%2C%20preciso%20de%20ajuda%20com%20a%20Simpler.";

const SIMPLER_SUPPORT_EMAIL =
  "contato@simplersolucoes.com.br";

const SUBJECTS = [
  {
    value: "suporte",
    label: "Suporte técnico",
  },
  {
    value: "plataforma",
    label: "Dúvidas sobre a plataforma",
  },
  {
    value: "comercial",
    label: "Comercial",
  },
];

const MODULES = [
  "Geral",
  "Mensagens",
  "Dashboard Federal",
  "Simples Nacional",
  "Parcelamentos",
  "Certidões",
  "CND Estadual",
  "Agenda Fiscal",
  "Saúde Fiscal",
  "Relatórios",
  "Sissi Assistente Fiscal",
  "Usuários",
];

const PRIORITIES = [
  {
    value: "normal",
    label: "Normal",
  },
  {
    value: "alta",
    label: "Alta",
  },
];

const CONTACT_OPTIONS = [
  {
    key: "technical",
    title: "Suporte técnico",
    description:
      "Erros, acessos, integrações, sincronizações e comportamento inesperado da plataforma.",
    action: "Abrir chamado",
    icon: Wrench,
    subject: "suporte",
  },
  {
    key: "platform",
    title: "Dúvidas da plataforma",
    description:
      "Ajuda sobre módulos, recursos, cadastros e funcionamento da Simpler.",
    action: "Pedir orientação",
    icon: CircleHelp,
    subject: "plataforma",
  },
  {
    key: "commercial",
    title: "Comercial",
    description:
      "Planos, novas empresas, contratação, expansão da carteira e novos módulos.",
    action: "Falar com comercial",
    icon: BriefcaseBusiness,
    subject: "comercial",
  },
];

export default function Atendimento() {
  const [subject, setSubject] = useState("suporte");
  const [module, setModule] = useState("Geral");
  const [priority, setPriority] = useState("normal");
  const [message, setMessage] = useState("");
  const [attachmentName, setAttachmentName] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [createdProtocol, setCreatedProtocol] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [replyMessage, setReplyMessage] = useState("");
  const [replySending, setReplySending] = useState(false);
  const [ticketActionError, setTicketActionError] = useState("");

  const selectedSubject = useMemo(
    () =>
      SUBJECTS.find(
        (item) => item.value === subject
      ) || SUBJECTS[0],
    [subject]
  );

  async function loadTickets() {
    setTicketsLoading(true);

    try {
      const response =
        await api.get("/support/tickets");

      setTickets(
        response?.data?.items ||
        response?.items ||
        []
      );
    } catch (error) {
      console.error(
        "Não foi possível carregar os chamados",
        error
      );
    } finally {
      setTicketsLoading(false);
    }
  }

  useEffect(() => {
    loadTickets();
  }, []);

  async function openTicket(ticketId) {
    setTicketLoading(true);
    setTicketActionError("");

    try {
      const response =
        await api.get(
          `/support/tickets/${ticketId}`
        );

      const ticket =
        response?.data ||
        response;

      setSelectedTicket(ticket);
      setReplyMessage("");
    } catch (error) {
      console.error(
        "Erro ao abrir chamado",
        error
      );

      setTicketActionError(
        error?.response?.data?.detail ||
        "Não foi possível abrir o chamado."
      );
    } finally {
      setTicketLoading(false);
    }
  }

  async function sendTicketReply(event) {
    event.preventDefault();

    if (
      !selectedTicket?.id ||
      !replyMessage.trim() ||
      replySending
    ) {
      return;
    }

    setReplySending(true);
    setTicketActionError("");

    try {
      await api.post(
        `/support/tickets/${selectedTicket.id}/messages`,
        {
          mensagem: replyMessage.trim(),
        }
      );

      setReplyMessage("");

      await openTicket(
        selectedTicket.id
      );

      await loadTickets();
    } catch (error) {
      console.error(
        "Erro ao responder chamado",
        error
      );

      setTicketActionError(
        error?.response?.data?.detail ||
        "Não foi possível enviar a mensagem."
      );
    } finally {
      setReplySending(false);
    }
  }

  async function cancelTicket() {
    if (!selectedTicket?.id) {
      return;
    }

    const confirmed = window.confirm(
      "Deseja cancelar esta solicitação? O chamado será encerrado."
    );

    if (!confirmed) {
      return;
    }

    setTicketActionError("");

    try {
      const response =
        await api.patch(
          `/support/tickets/${selectedTicket.id}/cancel`
        );

      const ticket =
        response?.data?.ticket ||
        response?.ticket;

      setSelectedTicket(ticket);

      await loadTickets();
    } catch (error) {
      console.error(
        "Erro ao cancelar chamado",
        error
      );

      setTicketActionError(
        error?.response?.data?.detail ||
        "Não foi possível cancelar o chamado."
      );
    }
  }

  function chooseContact(item) {
    setSubject(item.subject);
    setShowForm(true);

    window.setTimeout(() => {
      document
        .getElementById("support-form")
        ?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
    }, 50);
  }

  async function submitRequest(event) {
    event.preventDefault();

    if (!message.trim() || sending) {
      return;
    }

    setSending(true);
    setSubmitError("");
    setCreatedProtocol("");

    try {
      const response = await api.post(
        "/support/tickets",
        {
          categoria: subject,
          modulo: module,
          prioridade: priority,
          mensagem: message.trim(),
        }
      );

      const ticket =
        response?.data?.ticket ||
        response?.ticket;

      setCreatedProtocol(
        ticket?.protocolo || ""
      );

      setMessage("");
      setAttachmentName("");
      setShowForm(false);

      await loadTickets();
    } catch (error) {
      console.error(
        "Erro ao criar chamado",
        error
      );

      setSubmitError(
        error?.response?.data?.detail ||
        "Não foi possível enviar sua solicitação. Tente novamente."
      );
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      className="space-y-6 animate-fade-up"
      data-testid="atendimento-page"
    >
      {/* Header */}
      <header className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="mt-2 font-display text-2xl font-semibold tracking-[-0.035em] text-foreground md:text-[30px]">
            Fale Conosco
          </h1>

        </div>

        <div className="flex items-center gap-3 rounded-xl border border-primary/15 bg-primary/5 px-4 py-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Headphones className="h-5 w-5" />
          </div>

          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Atendimento Simpler
            </div>

            <div className="mt-0.5 text-sm font-semibold text-foreground">
              Estamos aqui para ajudar
            </div>
          </div>
        </div>
      </header>

      {/* Canais oficiais */}
      <section className="overflow-hidden rounded-2xl border border-border bg-card">
        <div className="grid lg:grid-cols-[1fr_auto]">
          <div className="p-6 md:p-7">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-primary">
              Atendimento Simpler
            </div>

            <h2 className="mt-2 text-xl font-semibold tracking-[-0.02em] text-foreground">
              Fale diretamente com nosso time
            </h2>

            <p className="mt-2 max-w-xl text-sm leading-6 text-muted-foreground">
              Escolha o canal mais conveniente para falar
              com a equipe Simpler. Para questões que precisam
              de acompanhamento, você também pode abrir uma
              solicitação pela plataforma.
            </p>

            <div className="mt-5 flex flex-wrap gap-2">
              <a
                href={SIMPLER_WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-10 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
              >
                <Phone className="h-4 w-4" />
                Falar pelo WhatsApp
                <ExternalLink className="h-3.5 w-3.5 opacity-70" />
              </a>

              <a
                href={`mailto:${SIMPLER_SUPPORT_EMAIL}?subject=Suporte%20Simpler`}
                className="inline-flex h-10 items-center gap-2 rounded-lg border border-border bg-card px-4 text-sm font-semibold text-foreground transition-colors hover:border-primary/25 hover:bg-primary/[0.03] hover:text-primary"
              >
                <Mail className="h-4 w-4 text-primary" />
                Enviar e-mail
              </a>
            </div>
          </div>

          <div className="flex min-w-[290px] flex-col justify-center border-t border-border bg-primary/[0.035] p-6 lg:border-l lg:border-t-0">
            <div className="space-y-4">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  WhatsApp
                </div>

                <div className="mt-1 text-base font-semibold text-foreground">
                  {SIMPLER_WHATSAPP_DISPLAY}
                </div>
              </div>

              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  E-mail de suporte
                </div>

                <a
                  href={`mailto:${SIMPLER_SUPPORT_EMAIL}`}
                  className="mt-1 block text-sm font-semibold text-primary hover:underline"
                >
                  {SIMPLER_SUPPORT_EMAIL}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Caminhos */}
      <section className="grid gap-3 lg:grid-cols-3">
        {CONTACT_OPTIONS.map((item) => {
          const Icon = item.icon;

          return (
            <button
              key={item.key}
              type="button"
              onClick={() => chooseContact(item)}
              className="group flex min-h-[180px] flex-col rounded-2xl border border-border bg-card p-5 text-left transition-colors hover:border-primary/25 hover:bg-primary/[0.02]"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-primary/15 bg-primary/5 text-primary">
                <Icon className="h-4.5 w-4.5" />
              </div>

              <div className="mt-5 text-base font-semibold tracking-[-0.01em] text-foreground">
                {item.title}
              </div>

              <div className="mt-2 flex-1 text-sm leading-6 text-muted-foreground">
                {item.description}
              </div>

              <div className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-primary">
                {item.action}
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </div>
            </button>
          );
        })}
      </section>

      {/* Formulário */}
      <section
        id="support-form"
        className="overflow-hidden rounded-2xl border border-border bg-card"
      >
        <div className="flex flex-col gap-3 border-b border-border px-5 py-5 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <LifeBuoy className="h-4 w-4 text-primary" />

              <h2 className="text-base font-semibold tracking-[-0.01em] text-foreground">
                Enviar solicitação
              </h2>
            </div>

            <p className="mt-1 text-sm text-muted-foreground">
              Informe o assunto e descreva o que você
              precisa.
            </p>
          </div>

          {!showForm && (
            <button
              type="button"
              onClick={() => setShowForm(true)}
              className="inline-flex h-9 items-center justify-center rounded-lg bg-primary px-4 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90"
            >
              Nova solicitação
            </button>
          )}
        </div>

        {showForm ? (
          <form
            onSubmit={submitRequest}
            className="space-y-5 p-5"
          >
            <div className="grid gap-4 md:grid-cols-3">
              <Field label="Assunto">
                <SelectShell>
                  <select
                    value={subject}
                    onChange={(event) =>
                      setSubject(event.target.value)
                    }
                    className="h-full w-full appearance-none bg-transparent px-3 pr-9 text-sm outline-none"
                  >
                    {SUBJECTS.map((item) => (
                      <option
                        key={item.value}
                        value={item.value}
                      >
                        {item.label}
                      </option>
                    ))}
                  </select>
                </SelectShell>
              </Field>

              <Field label="Módulo">
                <SelectShell>
                  <select
                    value={module}
                    onChange={(event) =>
                      setModule(event.target.value)
                    }
                    className="h-full w-full appearance-none bg-transparent px-3 pr-9 text-sm outline-none"
                  >
                    {MODULES.map((item) => (
                      <option
                        key={item}
                        value={item}
                      >
                        {item}
                      </option>
                    ))}
                  </select>
                </SelectShell>
              </Field>

              <Field label="Prioridade">
                <SelectShell>
                  <select
                    value={priority}
                    onChange={(event) =>
                      setPriority(event.target.value)
                    }
                    className="h-full w-full appearance-none bg-transparent px-3 pr-9 text-sm outline-none"
                  >
                    {PRIORITIES.map((item) => (
                      <option
                        key={item.value}
                        value={item.value}
                      >
                        {item.label}
                      </option>
                    ))}
                  </select>
                </SelectShell>
              </Field>
            </div>

            <Field label="Como podemos ajudar?">
              <textarea
                value={message}
                onChange={(event) =>
                  setMessage(event.target.value)
                }
                rows={5}
                placeholder={`Descreva sua solicitação sobre ${selectedSubject.label.toLowerCase()}...`}
                className="w-full resize-none rounded-xl border border-border bg-background px-4 py-3 text-sm leading-6 outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-primary/40"
              />
            </Field>

            <div className="flex flex-col gap-4 border-t border-border pt-5 md:flex-row md:items-center md:justify-between">
              <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-medium text-muted-foreground transition-colors hover:text-primary">
                <Paperclip className="h-4 w-4" />

                {attachmentName ||
                  "Anexar print ou documento"}

                <input
                  type="file"
                  className="hidden"
                  onChange={(event) =>
                    setAttachmentName(
                      event.target.files?.[0]?.name || ""
                    )
                  }
                />
              </label>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="inline-flex h-9 items-center rounded-lg border border-border px-4 text-xs font-semibold text-foreground transition-colors hover:border-primary/25 hover:text-primary"
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  disabled={!message.trim() || sending}
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-4 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Send className="h-3.5 w-3.5" />
                  {sending
                    ? "Enviando..."
                    : "Enviar solicitação"}
                </button>
              </div>
            </div>

            {submitError && (
              <div className="rounded-xl border border-critical/20 bg-critical/5 px-4 py-3 text-xs text-critical">
                {submitError}
              </div>
            )}
          </form>
        ) : (
          <div className="flex min-h-[150px] flex-col items-center justify-center px-5 py-8 text-center">
            <MessageCircle className="h-6 w-6 text-primary" />

            <div className="mt-3 text-sm font-semibold text-foreground">
              Precisa falar com a Simpler?
            </div>

            <div className="mt-1 max-w-lg text-sm text-muted-foreground">
              Abra uma solicitação e informe os detalhes
              para que o atendimento seja direcionado
              corretamente.
            </div>
          </div>
        )}
      </section>

      {createdProtocol && (
        <div className="flex items-center justify-between gap-4 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3">
          <div>
            <div className="text-xs font-semibold text-primary">
              Solicitação enviada com sucesso
            </div>

            <div className="mt-0.5 text-sm text-foreground">
              Protocolo{" "}
              <span className="font-mono font-semibold">
                {createdProtocol}
              </span>
            </div>
          </div>

          <CheckCircle2 className="h-5 w-5 text-primary" />
        </div>
      )}

      {/* Chamados */}
      <section className="overflow-hidden rounded-2xl border border-border bg-card">
        <div className="border-b border-border px-5 py-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-base font-semibold text-foreground">
                Meus chamados
              </h2>

              <p className="mt-1 text-sm text-muted-foreground">
                Acompanhe suas solicitações enviadas para
                o time Simpler.
              </p>
            </div>
          </div>
        </div>

        {ticketsLoading ? (
          <div className="px-5 py-8 text-center text-sm text-muted-foreground">
            Carregando chamados...
          </div>
        ) : tickets.length === 0 ? (
          <div className="flex min-h-[190px] flex-col items-center justify-center px-5 py-8 text-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <CheckCircle2 className="h-5 w-5" />
            </div>

            <div className="mt-3 text-sm font-semibold text-foreground">
              Nenhum chamado aberto
            </div>

            <div className="mt-1 max-w-md text-sm leading-6 text-muted-foreground">
              Quando você abrir uma solicitação,
              o andamento aparecerá aqui.
            </div>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {tickets.map((ticket) => (
              <button
                key={ticket.id}
                type="button"
                onClick={() => openTicket(ticket.id)}
                className="flex w-full flex-col gap-3 px-5 py-4 text-left transition-colors hover:bg-primary/[0.025] md:flex-row md:items-center md:justify-between"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-xs font-semibold text-primary">
                      {ticket.protocolo}
                    </span>

                    <span className="rounded-full border border-primary/15 bg-primary/5 px-2 py-0.5 text-[10px] font-semibold uppercase text-primary">
                      {ticket.status === "aberto"
                        ? "Aberto"
                        : ticket.status === "em_analise"
                        ? "Em análise"
                        : ticket.status === "cancelado"
                        ? "Cancelado"
                        : "Resolvido"}
                    </span>
                  </div>

                  <div className="mt-1 text-sm font-semibold text-foreground">
                    {ticket.modulo || "Geral"}
                  </div>

                  <div className="mt-1 max-w-2xl truncate text-xs text-muted-foreground">
                    {ticket.mensagem}
                  </div>
                </div>

                <div className="shrink-0 text-xs text-muted-foreground">
                  {ticket.created_at
                    ? new Date(
                        ticket.created_at
                      ).toLocaleString(
                        "pt-BR"
                      )
                    : ""}
                </div>
                <ChevronRight className="hidden h-4 w-4 shrink-0 text-muted-foreground md:block" />
              </button>
            ))}
          </div>
        )}
      </section>
      {selectedTicket && (
        <ScreenOverlay><div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setSelectedTicket(null);
            }
          }}
        >
          <div
            className="flex max-h-[88vh] w-full max-w-[760px] flex-col overflow-hidden rounded-2xl border border-border bg-background"
            role="dialog"
            aria-modal="true"
            aria-label="Detalhes do chamado"
          >
            <div className="flex items-start justify-between gap-4 border-b border-border px-6 py-5">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs font-semibold text-primary">
                    {selectedTicket.protocolo}
                  </span>

                  <span className="rounded-full border border-primary/15 bg-primary/5 px-2 py-0.5 text-[10px] font-semibold uppercase text-primary">
                    {selectedTicket.status === "aberto"
                      ? "Aberto"
                      : selectedTicket.status === "em_analise"
                      ? "Em análise"
                      : selectedTicket.status === "cancelado"
                      ? "Cancelado"
                      : "Resolvido"}
                  </span>
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
                  <h2 className="text-lg font-semibold tracking-[-0.02em] text-foreground">
                    {selectedTicket.modulo || "Geral"}
                  </h2>

                  <span className="text-xs text-muted-foreground">
                    ·
                  </span>

                  <span className="text-xs text-muted-foreground">
                    {selectedTicket.created_at
                      ? new Date(
                          selectedTicket.created_at
                        ).toLocaleString(
                          "pt-BR"
                        )
                      : ""}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setSelectedTicket(null)}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
                aria-label="Fechar chamado"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto bg-muted/[0.16] px-6 py-6">
              {ticketLoading ? (
                <div className="flex min-h-[220px] items-center justify-center text-sm text-muted-foreground">
                  Carregando chamado...
                </div>
              ) : (
                <div className="mx-auto max-w-[680px] space-y-4">
                  {(selectedTicket.messages || []).map(
                    (item) => {
                      const fromSupport =
                        item.author_type === "support";

                      return (
                        <div
                          key={item.id}
                          className={
                            fromSupport
                              ? "ml-auto max-w-[86%]"
                              : "mr-auto max-w-[86%]"
                          }
                        >
                          <div
                            className={
                              fromSupport
                                ? "rounded-2xl rounded-br-md border border-primary/15 bg-primary/[0.055] px-4 py-3"
                                : "rounded-2xl rounded-bl-md border border-border bg-card px-4 py-3"
                            }
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div
                                className={
                                  fromSupport
                                    ? "text-xs font-semibold text-primary"
                                    : "text-xs font-semibold text-foreground"
                                }
                              >
                                {fromSupport
                                  ? "Equipe Simpler"
                                  : item.user_name ||
                                    "Você"}
                              </div>

                              <div className="text-[10px] text-muted-foreground">
                                {item.created_at
                                  ? new Date(
                                      item.created_at
                                    ).toLocaleString(
                                      "pt-BR"
                                    )
                                  : ""}
                              </div>
                            </div>

                            <div className="mt-2 whitespace-pre-wrap text-sm leading-6 text-foreground">
                              {item.message}
                            </div>
                          </div>
                        </div>
                      );
                    }
                  )}

                  {(selectedTicket.messages || []).length === 0 && (
                    <div className="flex min-h-[220px] items-center justify-center text-sm text-muted-foreground">
                      Nenhuma mensagem neste chamado.
                    </div>
                  )}
                </div>
              )}

              {ticketActionError && (
                <div className="mx-auto mt-4 max-w-[680px] rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-xs text-destructive">
                  {ticketActionError}
                </div>
              )}
            </div>

            {selectedTicket.status === "resolvido" ||
            selectedTicket.status === "cancelado" ? (
              <div className="border-t border-border bg-card px-6 py-5">
                <div className="mx-auto flex max-w-[680px] items-start gap-3 rounded-xl border border-primary/15 bg-primary/[0.035] px-4 py-3">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />

                  <div>
                    <div className="text-sm font-semibold text-foreground">
                      {selectedTicket.status === "cancelado"
                        ? "Solicitação cancelada"
                        : "Solicitação resolvida"}
                    </div>

                    <p className="mt-1 text-xs leading-5 text-muted-foreground">
                      Este atendimento está encerrado.
                      O histórico permanece disponível para consulta.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <form
                onSubmit={sendTicketReply}
                className="border-t border-border bg-card px-6 py-5"
              >
                <div className="mx-auto max-w-[680px]">
                  <textarea
                    value={replyMessage}
                    onChange={(event) =>
                      setReplyMessage(
                        event.target.value
                      )
                    }
                    rows={3}
                    placeholder="Escreva uma mensagem para o atendimento..."
                    className="w-full resize-none rounded-xl border border-border bg-background px-4 py-3 text-sm leading-6 outline-none transition-colors placeholder:text-muted-foreground focus:border-primary/40"
                  />

                  <div className="mt-3 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <button
                      type="button"
                      onClick={cancelTicket}
                      className="inline-flex h-9 items-center justify-center rounded-lg border border-border px-4 text-xs font-semibold text-muted-foreground transition-colors hover:border-destructive/30 hover:text-destructive"
                    >
                      Cancelar solicitação
                    </button>

                    <button
                      type="submit"
                      disabled={
                        !replyMessage.trim() ||
                        replySending
                      }
                      className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <CornerDownLeft className="h-3.5 w-3.5" />

                      {replySending
                        ? "Enviando..."
                        : "Enviar mensagem"}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div></ScreenOverlay>
      )}

    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </span>

      {children}
    </label>
  );
}

function SelectShell({ children }) {
  return (
    <div className="relative h-10 rounded-xl border border-border bg-background transition-colors focus-within:border-primary/40">
      {children}

      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
    </div>
  );
}
